import React, { useEffect, useRef } from "react";
import "./UnityGame.css";

type UnityGameProps = {
  buildPath: string;
};

interface UnityInstance {
  SendMessage(gameObject: string, methodName: string, parameter?: string): void;
}

declare function createUnityInstance(
  canvas: HTMLCanvasElement,
  config: {
    dataUrl: string;
    frameworkUrl: string;
    codeUrl: string;
    streamingAssetsUrl: string;
    webglContextAttributes?: WebGLContextAttributes;
  },
): Promise<UnityInstance>;

declare global {
  interface Window {
    unityInstance?: UnityInstance;
    OnBridgeReady?: () => void;
  }
}

export default function UnityGamePublic({ buildPath }: UnityGameProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const gameFolder = buildPath.replace(/^\/+|\/+$/g, "");
    const baseUrl = `/RLPages/${gameFolder}`;
    const prefix = gameFolder;

    const script = document.createElement("script");
    script.src = `${baseUrl}/Build/${prefix}.loader.js`;
    const canvas = canvasRef.current;

    script.onload = () => {
      setTimeout(() => {
        // 🔒 GPU-safe sizing
        const testCanvas = document.createElement("canvas");
        const gl =
          testCanvas.getContext("webgl") || testCanvas.getContext("webgl2");
        const maxSize = gl ? gl.getParameter(gl.MAX_TEXTURE_SIZE) : 4096;

        const MAX_TEXTURE_SIZE = Math.min(maxSize / 2, 4096);

        const cssWidth = canvas.clientWidth || 960;
        const cssHeight = canvas.clientHeight || 600;

        canvas.width = Math.min(cssWidth, MAX_TEXTURE_SIZE);
        canvas.height = Math.min(cssHeight, MAX_TEXTURE_SIZE);

        console.log(
          `Canvas set to ${canvas.width}×${canvas.height} (max GPU ${maxSize})`,
        );

        createUnityInstance(canvas, {
          dataUrl: `${baseUrl}/Build/${prefix}.data`,
          frameworkUrl: `${baseUrl}/Build/${prefix}.framework.js`,
          codeUrl: `${baseUrl}/Build/${prefix}.wasm`,
          streamingAssetsUrl: `${baseUrl}/StreamingAssets`,
          webglContextAttributes: {
            preserveDrawingBuffer: false,
            powerPreference: "high-performance",
          },
        })
          .then((unityInstance) => {
            window.unityInstance = unityInstance;
            console.log("✅ Unity instance created");

            const currentCanvas = canvasRef.current;
            if (!currentCanvas) return;

            const handleWheel = (e: WheelEvent) => {
              // Allow page scroll while hovering Unity
              window.scrollBy({
                top: e.deltaY,
                behavior: "auto",
              });
            };

            currentCanvas.addEventListener("wheel", handleWheel, {
              passive: true,
            });

            // Cleanup if component unmounts
            return () => {
              currentCanvas.removeEventListener("wheel", handleWheel);
            };
          })
          .catch((err) => {
            console.error("❌ Unity init failed:", err);
          });
      }, 100);
    };

    script.onerror = () => {
      console.error("❌ Failed to load Unity loader script");
    };

    document.body.appendChild(script);

    return () => {
      delete window.unityInstance;
      if (script.parentNode) {
        document.body.removeChild(script);
      }
    };
  }, [buildPath]);

  useEffect(() => {
    console.log("🎮 UnityGame MOUNT");
    return () => {
      console.log("🧹 UnityGame UNMOUNT");
    };
  }, [buildPath]);

  return (
    <div className="unity-container">
      <canvas ref={canvasRef} id="unity-canvas" />
    </div>
  );
}
